import React, { useEffect, useRef, useState } from 'react';
import useIntl from '@afosto/utils-react/intl/useIntl';
import useOrderState from '@afosto/utils-react/useOrderState';
import usePrivate from '@afosto/auth-react/usePrivate';
import { Link, useHistory, useParams } from 'react-router-dom';
import Button from '@material-ui/core/Button';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import OrderForm from '../../components/OrderForm';
import OrderFormProvider from '../../components/OrderFormProvider';
import Dialog from '@afosto/app-components-react/Dialog';
import Page from '@afosto/app-components-react/Page';
import SkeletonPage from '@afosto/app-components-react/SkeletonPage';
import findObjectInArray from '@afosto/utils/array/findObjectInArray';
import getContactDisplay from '@afosto/utils/getContactDisplay';
import { addresses as addressesApi } from '@afosto/contacts/api/Addresses';
import { contacts as contactsApi } from '@afosto/contacts/api/Contacts';
import { fulfilment as fulfilmentApi } from '@afosto/fulfilment/api/Fulfilment';
import { methods as methodsApi } from '@afosto/fulfilment/api/Methods';
import { oauth as oauthApi } from '@afosto/iam/api/Oauth';
import { orders as ordersApi } from '@afosto/order/api/Orders';
import { organisations as organisationsApi } from '@afosto/contacts/api/Organisations';
import { payments as paymentsApi } from '@afosto/order/api/Payments';
import { profile as profileApi } from '@afosto/iam/api/Profile';
import translations from './translations';

const ORDER_TAGS_PATH = 'afosto.tenant.odr.orders.tags';

const EditOrder = () => {
  usePrivate();

  const history = useHistory();
  const intl = useIntl();
  const { id: orderId } = useParams();
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);
  const orderState = useOrderState();
  const orderStateRef = useRef(orderState);
  const { isLoadingOrder, order } = orderState;

  useEffect(() => {
    orderStateRef.current = orderState;
  });

  useEffect(() => {
    let isMounted = true;

    const {
      setAvailableAddresses,
      setBilling,
      setBillingAddress,
      setClient,
      setContact,
      setCoupons,
      setIsLoadingOrder,
      setOrder,
      setOrderTags,
      setOrganisation,
      setShipping,
      setShippingAddress,
      setReference,
    } = orderStateRef?.current ?? {};

    const fetchOrder = async id => {
      try {
        const response = await ordersApi.getOrder(id);
        const { data = null } = response;

        return data;
      } catch (err) {
        return Promise.reject(err);
      }
    };

    const fetchClient = async clientId => {
      try {
        const response = await oauthApi.getClient(clientId);
        const { data } = response;
        const clientData = { ...data, label: data?.name, value: data.client_id };

        if (isMounted) {
          setClient(clientData);
        }

        return clientData;
      } catch (err) {
        if (isMounted) {
          setClient(null);
        }

        return Promise.reject(err);
      }
    };

    const fetchContact = async contactId => {
      try {
        const response = await contactsApi.getContact(contactId);
        const { data } = response;
        const contactData = { ...data, label: getContactDisplay(data), value: data.id };

        if (isMounted) {
          setContact(contactData);
        }

        return contactData;
      } catch (err) {
        if (isMounted) {
          setContact(null);
        }

        return Promise.reject(err);
      }
    };

    const fetchOrganisation = async organisationId => {
      try {
        const response = await organisationsApi.viewOrganisation(organisationId);
        const { data } = response;
        const organisationData = {
          ...data,
          label: data.name,
          description: data.coc_number,
          value: data.id,
        };

        if (isMounted) {
          setOrganisation(organisationData);
        }

        return organisationData;
      } catch (err) {
        if (isMounted) {
          setOrganisation(null);
        }

        return Promise.reject(err);
      }
    };

    const fetchBillingAddress = async addressId => {
      try {
        const response = await addressesApi.viewAddress(addressId);
        const { data } = response;

        if (isMounted) {
          setBillingAddress(data);
        }

        return data;
      } catch (err) {
        if (isMounted) {
          setBillingAddress(null);
        }

        return Promise.reject(err);
      }
    };

    const fetchShippingAddress = async addressId => {
      try {
        const response = await addressesApi.viewAddress(addressId);
        const { data } = response;

        if (isMounted) {
          setShippingAddress(data);
        }

        return data;
      } catch (err) {
        if (isMounted) {
          setShippingAddress(null);
        }

        return Promise.reject(err);
      }
    };

    const fetchShippingMethod = async id => {
      try {
        if (!id) {
          return null;
        }

        const response = await methodsApi.getShippingMethod(id);
        const { data = null } = response;

        return data;
      } catch (err) {
        return Promise.reject(err);
      }
    };

    const fetchServicePoint = async (spid, method, address) => {
      try {
        const response = await fulfilmentApi.getServicePoint(
          spid,
          method?.carrier_code,
          address?.country_code,
        );
        const { data = null } = response;

        return data;
      } catch (err) {
        return Promise.reject(err);
      }
    };

    const fetchShipping = async orderData => {
      try {
        const { shipping: shippingInformation } = orderData;
        if (!shippingInformation?.address_id) {
          if (isMounted) {
            setShipping(null);
          }

          return null;
        }

        const shippingAddressData = await fetchShippingAddress(shippingInformation.address_id);
        let shippingMethodData = null;
        let servicePoint = null;

        if (shippingInformation?.method_id) {
          shippingMethodData = await fetchShippingMethod(shippingInformation.method_id);
        }

        if (shippingInformation?.spid && shippingAddressData?.id && shippingMethodData?.id) {
          servicePoint = await fetchServicePoint(
            shippingInformation.spid,
            shippingMethodData,
            shippingAddressData,
          );
        }

        const shippingData = {
          method: shippingMethodData,
          servicePoint,
          isSplitAllowed: shippingInformation?.is_split_allowed,
          deliveryAt: shippingInformation?.window?.delivery_at ?? null,
        };

        if (isMounted) {
          setShipping(shippingData);
        }

        return shippingData;
      } catch (err) {
        if (isMounted) {
          setShipping(null);
        }

        return Promise.reject(err);
      }
    };

    const fetchPaymentMethod = async (providerCode, methodCode, issuerCode) => {
      try {
        const response = await paymentsApi.getPaymentMethod(providerCode, methodCode);
        const { data = null } = response;
        const issuer = findObjectInArray(data?.issuers ?? [], 'code', issuerCode);

        return {
          method: {
            ...data,
            label: data?.name,
            value: data?.code,
          },
          issuer,
          provider: providerCode,
        };
      } catch (err) {
        return Promise.reject(err);
      }
    };

    const fetchBilling = async billingInformation => {
      try {
        let billing = null;

        if (billingInformation?.address_id) {
          await fetchBillingAddress(billingInformation.address_id);
        }

        if (billingInformation?.provider_code && billingInformation?.method_code) {
          const {
            provider_code: providerCode,
            method_code: methodCode,
            issuer_code: issuerCode,
          } = billingInformation;
          billing = await fetchPaymentMethod(providerCode, methodCode, issuerCode);
        }

        if (isMounted) {
          setBilling(billing);
        }

        return billing;
      } catch (err) {
        if (isMounted) {
          setBilling(null);
        }

        return Promise.reject(err);
      }
    };

    const fetchContactOrganisationData = async ({ contact, organisation }) => {
      try {
        const promises = [
          ...(contact?.id ? [fetchContact(contact.id)] : []),
          ...(organisation?.id ? [fetchOrganisation(organisation.id)] : []),
        ];

        const [contactData, organisationData] = await Promise.all(promises);

        if (isMounted) {
          setAvailableAddresses({
            contact: { shipping: contactData?.shipping, billing: contactData?.billing },
            organisation: {
              shipping: organisationData?.shipping,
              billing: organisationData?.billing,
            },
          });
        }

        return {
          contact: contactData,
          organisation: organisationData,
        };
      } catch (err) {
        return Promise.reject(err);
      }
    };

    const fetchOrderData = async () => {
      try {
        setIsLoadingOrder(true);

        const currentOrder = await fetchOrder(orderId);
        const orderSettings = currentOrder?.settings ?? {};
        const promises = [
          ...(currentOrder?.client_id ? [fetchClient(currentOrder.client_id)] : []),
          fetchContactOrganisationData(orderSettings),
          ...(orderSettings?.billing ? [fetchBilling(orderSettings.billing)] : []),
          ...(orderSettings?.shipping ? [fetchShipping(orderSettings)] : []),
        ];

        await Promise.all(promises);

        let orderTags = [];
        if (currentOrder?.tags?.length > 0) {
          const { data: orderTagsData } = await profileApi.getProfile(ORDER_TAGS_PATH);
          orderTags = orderTagsData || [];
        }

        const formattedTags = orderTags.reduce(
          (acc, tag) => [...acc, { ...tag, value: tag?.code }],
          [],
        );

        if (isMounted) {
          setReference(currentOrder?.settings?.reference);
          setOrderTags(formattedTags.filter(tag => currentOrder.tags.includes(tag?.code)));
          setOrder(currentOrder);
          setCoupons(currentOrder?.settings?.coupons ?? []);
        }
      } catch (err) {
        if (isMounted) {
          setOrder(null);
        }
      } finally {
        if (isMounted) {
          setIsLoadingOrder(false);
        }
      }
    };

    if (orderId) {
      fetchOrderData();
    }

    return () => {
      isMounted = false;
    };
  }, [orderId]);

  if (isLoadingOrder) {
    return <SkeletonPage breadcrumbs={3} />;
  }

  if (!order) {
    return <h1>No order found!</h1>;
  }

  if (order?.is_confirmed) {
    return <h1>Order is not editable!</h1>;
  }

  return (
    <Page
      title={intl.formatMessage(translations.title)}
      breadcrumbs={[
        { label: intl.formatMessage(translations.breadcrumbs.orders), path: '/' },
        { label: order?.number ?? 'Concept', path: `/orders/${order.id}` },
        {
          label: intl.formatMessage(translations.breadcrumbs.edit),
          path: `/orders/edit/${order.id}`,
          active: true,
        },
      ]}
      linkComponent={Link}
    >
      <OrderFormProvider value={{ ...orderState, type: order?.type }}>
        <OrderForm
          onCancel={() => setShowConfirmCancel(true)}
          onSave={() => history.push(`/orders/${order.id}`)}
        />
      </OrderFormProvider>
      <Dialog open={showConfirmCancel} onClose={() => setShowConfirmCancel(false)}>
        <DialogTitle>{intl.formatMessage(translations.cancelDialog.title)}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {intl.formatMessage(translations.cancelDialog.description)}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmCancel(false)} color="secondary">
            {intl.formatMessage(translations.cancelDialog.actions.cancel)}
          </Button>
          <Button onClick={() => history.push(`/orders/${order.id}`)} color="primary" autoFocus>
            {intl.formatMessage(translations.cancelDialog.actions.ok)}
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
};

export default EditOrder;
